import type { CallUpGroup } from '@/lib/callUp';
import { sheetName } from '@/lib/format';

/**
 * The call-up, as a club issues one.
 *
 * This is the one document that leaves the building. It goes into a group chat
 * and is read on a phone in ten seconds, so it is shaped 4:5 rather than A4 —
 * a portrait page arrives in WhatsApp as a tall thumbnail nobody can read, and
 * the point of the thing is that nobody has to open it to see whether their name
 * is on it.
 *
 * Formal on purpose. The club's colour is in the rules and the number discs and
 * nowhere else: a call-up is an instruction, and it carries more weight looking
 * like a team sheet than like a poster. Nothing on it means anything by colour
 * alone, so a black and white print on the noticeboard says the same thing.
 */

export interface CallUpMeta {
  clubName: string;
  teamName?: string;
  coachName?: string;
  logoUrl?: string | null;
  accent?: string | null;
  /** Rendered date, for the footer. */
  issuedOn: string;
}

export interface CallUpFixture {
  opponent?: string | null;
  /** Already formatted in the active locale. */
  dateLabel: string;
  kickOff?: string | null;
  venueLabel?: string | null;
  competition?: string | null;
}

export interface CallUpDetails {
  meetAt?: string | null;
  meetPlace?: string | null;
  kit?: string | null;
  note?: string | null;
  /** The objective, when the coach chooses to publish it. */
  whatWeWantToSee?: string | null;
}

export interface CallUpLabels {
  title: string;
  /** The word before the coach's name. A call-up is signed by somebody. */
  coach: string;
  /** Goes before the opponent: "vs Wydad AC". */
  versus: string;
  meet: string;
  kit: string;
  starting: string;
  bench: string;
  squad: string;
  whatWeWantToSee: string;
  empty: string;
}

export function CallUpSheet({
  meta,
  fixture,
  details,
  groups,
  labels,
}: {
  meta: CallUpMeta;
  fixture: CallUpFixture;
  details: CallUpDetails;
  groups: CallUpGroup[];
  labels: CallUpLabels;
}) {
  const accent = meta.accent || '#1f2937';
  const total = groups.reduce((n, g) => n + g.players.length, 0);
  // Past fourteen names one column stops fitting, and two short columns read
  // better than one long one anyway.
  const twoColumns = total > 14;

  // The disc counts the call-up, it does not carry a shirt number: half a squad
  // has no number on a Tuesday, and a player reading this wants to know he is the
  // fourteenth of eighteen named, not what he will wear. The count runs through
  // the groups rather than restarting, so the last disc is the size of the squad.
  let counted = 0;
  const numbered = groups.map((group) => ({
    key: group.key,
    players: group.players.map((player) => ({ player, no: (counted += 1) })),
  }));

  const facts = [
    details.meetAt || details.meetPlace
      ? { label: labels.meet, value: [details.meetAt, details.meetPlace].filter(Boolean).join(' · ') }
      : null,
    details.kit ? { label: labels.kit, value: details.kit } : null,
    details.whatWeWantToSee
      ? { label: labels.whatWeWantToSee, value: details.whatWeWantToSee }
      : null,
  ].filter((f): f is { label: string; value: string } => f !== null);

  return (
    <article
      className="report-sheet"
      data-orientation="poster"
      style={{
        // 4:5 for a normal squad, and taller when there are more names than that
        // holds. A minimum rather than a height: a fixed box would push the last
        // few players under the footer, and a call-up that drops a name is worse
        // than one that is a centimetre too long.
        width: '120mm',
        minHeight: '150mm',
        padding: '9mm 10mm 8mm',
        background: '#fff',
        color: '#111827',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', gap: '4mm' }}>
        {meta.logoUrl ? (
          // The crest is the first thing a player recognises in a thread full of
          // messages, so it is sized to be seen in a thumbnail.
          <img
            src={meta.logoUrl}
            alt=""
            style={{ width: '20mm', height: '20mm', objectFit: 'contain' }}
            crossOrigin="anonymous"
          />
        ) : null}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: '4.8mm', fontWeight: 700, lineHeight: 1.15 }}>
            {labels.title}
          </div>
          {/* Club and team on one quiet line: a long club name on the title line
              pushed it to two and left the crest floating beside a wrap. */}
          <div
            style={{
              fontSize: '3.4mm',
              fontWeight: 600,
              letterSpacing: '0.2mm',
              color: '#4b5563',
              marginTop: '0.8mm',
            }}
          >
            {[meta.clubName, meta.teamName].filter(Boolean).join(' · ')}
          </div>
        </div>
      </header>

      <div style={{ height: '1.1mm', background: accent, margin: '3.5mm 0 4mm' }} />

      <section>
        {/* No opponent recorded yet means no opponent line. A bare dash where the
            fixture should be reads as something broken, not as something blank. */}
        {fixture.opponent ? (
          // No bigger than the club's own name above it. The opponent is who we
          // are playing, not whose sheet this is, and a long name set at display
          // size took over the page.
          <div style={{ fontSize: '4.6mm', fontWeight: 700, lineHeight: 1.25 }}>
            <span style={{ color: '#6b7280', fontWeight: 600 }}>{labels.versus} </span>
            {fixture.opponent}
          </div>
        ) : null}
        <div style={{ fontSize: '3.4mm', marginTop: '1mm', color: '#6b7280' }}>
          {[fixture.venueLabel, fixture.competition].filter(Boolean).join(' · ')}
        </div>
        <div style={{ fontSize: '4.4mm', marginTop: '1mm', fontWeight: 600 }}>
          {fixture.dateLabel}
          {fixture.kickOff ? ` · ${fixture.kickOff}` : ''}
        </div>
      </section>

      {facts.length > 0 ? (
        <dl
          style={{
            margin: '4mm 0 0',
            paddingTop: '3mm',
            borderTop: '0.3mm solid #d1d5db',
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '1.6mm 4mm',
            fontSize: '3.4mm',
          }}
        >
          {facts.map((f) => (
            <div key={f.label} style={{ display: 'contents' }}>
              <dt style={{ color: '#6b7280' }}>{f.label}</dt>
              <dd style={{ margin: 0, fontWeight: 600 }}>{f.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      <div
        style={{
          marginTop: '4mm',
          paddingTop: '3.5mm',
          borderTop: '0.3mm solid #d1d5db',
          // Takes the space that is left on a short list, and makes its own on a
          // long one. Either way the footer stays below it rather than under it.
          flex: 1,
        }}
      >
        {groups.length === 0 ? (
          <p style={{ margin: 0, fontSize: '3.4mm', color: '#9ca3af' }}>{labels.empty}</p>
        ) : (
          numbered.map((group) => (
            <section key={group.key} style={{ marginBottom: '2.5mm' }}>
              {group.key !== 'squad' ? (
                <h2
                  style={{
                    margin: '0 0 1.6mm',
                    fontSize: '3mm',
                    fontWeight: 700,
                    letterSpacing: '0.3mm',
                    color: accent,
                  }}
                >
                  {group.key === 'starting' ? labels.starting : labels.bench}
                </h2>
              ) : null}
              <ol
                style={{
                  margin: 0,
                  padding: 0,
                  listStyle: 'none',
                  display: 'grid',
                  gridTemplateColumns: twoColumns ? '1fr 1fr' : '1fr',
                  gap: '1.2mm 4mm',
                }}
              >
                {group.players.map(({ player: p, no }) => (
                  <li
                    key={p.playerId}
                    style={{ display: 'flex', alignItems: 'center', gap: '2.4mm', minWidth: 0 }}
                  >
                    <span
                      style={{
                        flex: 'none',
                        width: '5.4mm',
                        height: '5.4mm',
                        borderRadius: '50%',
                        background: accent,
                        border: `0.3mm solid ${accent}`,
                        color: '#fff',
                        fontSize: '2.9mm',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1,
                      }}
                    >
                      {no}
                    </span>
                    {/* Wrapping, not truncating. A player has to be able to find
                        his own name on this, and "Mohammed AMINE E…" is not it. */}
                    <span style={{ fontSize: '3.5mm', lineHeight: 1.25, minWidth: 0 }}>
                      {sheetName(p.firstName, p.lastName)}
                    </span>
                  </li>
                ))}
              </ol>
            </section>
          ))
        )}
      </div>

      {details.note ? (
        <p
          style={{
            margin: '0 0 3mm',
            fontSize: '3.3mm',
            lineHeight: 1.45,
            color: '#374151',
          }}
        >
          {details.note}
        </p>
      ) : null}

      <footer
        style={{
          paddingTop: '2.5mm',
          borderTop: `0.8mm solid ${accent}`,
          fontSize: '2.9mm',
          color: '#6b7280',
          display: 'flex',
          justifyContent: 'space-between',
          gap: '4mm',
        }}
      >
        <span>{meta.coachName ? `${labels.coach} ${meta.coachName}` : ''}</span>
        <span>{meta.issuedOn}</span>
      </footer>
    </article>
  );
}
